import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// Standard empty-state block used wherever a list would otherwise be replaced
// by a bare "No …" line. Keeps the visual rhythm of the page: same Card shape
// as the list-of-rows, plus a tinted icon tile that picks up the brand accent.

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  cta?:
    | { label: string; to: string }
    | { label: string; onClick: () => void };
  className?: string;
}

export function EmptyState({ icon: Icon, title, description, cta, className }: EmptyStateProps) {
  const hasLink = cta && "to" in cta;
  return (
    <Card className={cn("border-dashed", className)}>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <span
          aria-hidden
          className="grid h-12 w-12 place-items-center rounded-lg bg-accent text-accent-foreground"
        >
          <Icon className="h-5 w-5" />
        </span>
        <div className="space-y-1">
          <h3 className="text-base font-semibold">{title}</h3>
          {description && (
            <p className="mx-auto max-w-sm text-sm text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        {cta && hasLink && (
          <Button asChild size="sm" className="mt-2">
            <Link to={cta.to}>{cta.label}</Link>
          </Button>
        )}
        {cta && !hasLink && (
          <Button size="sm" onClick={cta.onClick} className="mt-2">
            {cta.label}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
